// Quest log panel — three tabs: Active / Available / Completed.
// Lists each quest with name, description, objectives + progress bars, rewards.

import type { Net } from "../net.js";
import type { ClientStore, ClientState, QuestLogEntry } from "../state.js";

type Tab = "active" | "available" | "completed";

const TAB_LABELS: Record<Tab, string> = {
  active: "Active",
  available: "Available",
  completed: "Completed",
};

export function openQuestLog(host: HTMLElement, net: Net, store: ClientStore): HTMLElement {
  const existing = host.querySelector<HTMLElement>(".quest-overlay");
  if (existing) { existing.remove(); return existing; }

  const overlay = document.createElement("div");
  overlay.className = "quest-overlay";
  host.appendChild(overlay);

  net.send({ t: "quests" });

  let activeTab: Tab = "active";

  function bucketize(qs: QuestLogEntry[]): Record<Tab, QuestLogEntry[]> {
    const out: Record<Tab, QuestLogEntry[]> = { active: [], available: [], completed: [] };
    for (const q of qs) {
      const status = q.status.toLowerCase();
      if (status === "completed" || status === "complete") out.completed.push(q);
      else if (status === "available" || status === "offered") out.available.push(q);
      else out.active.push(q);
    }
    return out;
  }

  function render(s: ClientState): void {
    const buckets = bucketize(s.quests);
    overlay.innerHTML = `
      <div class="quest-modal" role="dialog" aria-label="Quest Log">
        <header class="quest-header">
          <h2>Quest Log</h2>
          <span class="quest-count dim">${s.quests.length} total</span>
          <button id="quest-close" class="quest-close" aria-label="Close">×</button>
        </header>
        <nav class="quest-tabs">
          ${(Object.keys(TAB_LABELS) as Tab[]).map((tab) => `
            <button class="quest-tab ${tab === activeTab ? "active" : ""}" data-tab="${tab}">
              ${TAB_LABELS[tab]} <span class="dim">${buckets[tab].length}</span>
            </button>
          `).join("")}
        </nav>
        <section class="quest-list">
          ${buckets[activeTab].length === 0
            ? `<p class="dim quest-empty">No ${activeTab} quests.</p>`
            : buckets[activeTab].map((q) => renderQuest(q, activeTab)).join("")}
        </section>
      </div>
    `;

    for (const b of overlay.querySelectorAll<HTMLButtonElement>("button.quest-tab")) {
      b.addEventListener("click", () => { activeTab = b.dataset.tab as Tab; render(store.get()); });
    }
    for (const b of overlay.querySelectorAll<HTMLButtonElement>("[data-abandon]")) {
      b.addEventListener("click", () => net.send({ t: "abandon_quest", quest_id: Number(b.dataset.abandon) }));
    }
    for (const b of overlay.querySelectorAll<HTMLButtonElement>("[data-turnin]")) {
      b.addEventListener("click", () => net.send({ t: "turn_in", quest_id: Number(b.dataset.turnin) }));
    }
    overlay.querySelector<HTMLButtonElement>("#quest-close")?.addEventListener("click", () => cleanup());
  }

  function renderQuest(q: QuestLogEntry, tab: Tab): string {
    const objectives = q.progress.map((p) => {
      const pct = Math.max(0, Math.min(100, (p.have / Math.max(1, p.need)) * 100));
      return `
        <li class="quest-obj">
          <div class="quest-obj-label">${escape(p.kind)}: ${escape(p.target)}</div>
          <div class="quest-obj-track"><div class="quest-obj-fill" style="width:${pct.toFixed(1)}%"></div></div>
          <div class="quest-obj-count dim">${p.have} / ${p.need}</div>
        </li>
      `;
    }).join("");
    const actions: string[] = [];
    if (tab === "active") {
      actions.push(`<button class="quest-action" data-abandon="${q.id}">Abandon</button>`);
      if (q.ready_to_turn_in) actions.push(`<button class="quest-action primary" data-turnin="${q.id}">Turn in</button>`);
    }
    return `
      <article class="quest-entry ${q.ready_to_turn_in ? "ready" : ""}">
        <h3>${escape(q.name)}${q.ready_to_turn_in ? ' <span class="quest-ready">✓ ready</span>' : ""}</h3>
        <p class="quest-desc">${escape(q.description)}</p>
        ${objectives ? `<ul class="quest-objectives">${objectives}</ul>` : ""}
        ${actions.length > 0 ? `<div class="quest-actions">${actions.join("")}</div>` : ""}
      </article>
    `;
  }

  let cleanup: () => void = () => { /* set below */ };
  const unsubscribe = store.subscribe(render);
  render(store.get());
  cleanup = (): void => {
    unsubscribe();
    document.removeEventListener("keydown", onEsc);
    overlay.remove();
    store.closePanel();
  };
  function onEsc(ev: KeyboardEvent): void { if (ev.key === "Escape") cleanup(); }
  (overlay as unknown as { __cleanup: () => void }).__cleanup = cleanup;
  overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(); });
  document.addEventListener("keydown", onEsc);
  return overlay;
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
